import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { NavigationSidebar, useAuth, type SidebarGroup, type SidebarLink } from '@monecolier/ui';
import { getMe, listEnrollmentsForReview, type MembershipRole } from '@monecolier/api-client';
import {
  MANAGEMENT_ROLES,
  PAYMENT_ROLES,
  STUDENT_ROLES,
  TEACHER_ROLES,
  canAccessRoute,
  hasAnyRole,
} from '../lib/roles';
import { NAV, NAV_GROUP_LABELS } from '../lib/navigation';

/**
 * UX-17 — patron de navigation UNIFIÉ web-admin/web-parent : le MÉCANISME
 * (sidebar fixe desktop, tiroir mobile sous 640px) a été extrait vers
 * `@monecolier/ui#NavigationSidebar` (voir ce composant pour le détail
 * complet), REMPLAÇANT `BottomTabBar.tsx` (web-parent, UX-11, retiré par ce
 * même ticket — corrigeait une divergence de patron introduite sans ticket
 * préalable). Ce fichier reste le wrapper PROPRE à web-admin : il calcule
 * SES groupes/liens/badges à partir du rôle résolu (`GET /me`) et les passe
 * en props au composant partagé — AUCUN changement de comportement visible
 * par rapport à la version d'origine (UX-09/UX-14), migration pure.
 *
 * `badgeTestId` explicitement fourni sur les deux badges concernés
 * (`enrollments-pending-badge-menu`/`enrollments-pending-badge`) — préserve
 * EXACTEMENT les `data-testid` déjà utilisés par `Sidebar.test.tsx` (et par
 * tout autre fichier de test qui les référence, ex. `Enrollments.test.tsx`),
 * jamais le repli générique du composant partagé.
 */
export function Sidebar() {
  const { accessToken, apiClient, isAuthenticated } = useAuth();
  // AUT-26 : `roles` (union d'accès), remplace le `role` scalaire — un membre peut
  // cumuler plusieurs rôles, chaque garde ci-dessous doit vérifier TOUS ses rôles.
  const [roles, setRoles] = useState<MembershipRole[]>([]);
  const [pendingEnrollments, setPendingEnrollments] = useState<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setRoles([]);
      return;
    }

    let cancelled = false;
    getMe(apiClient, accessToken)
      .then((me) => {
        if (!cancelled) setRoles(me.roles);
      })
      .catch(() => {
        if (!cancelled) setRoles([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, accessToken, apiClient, location.pathname]);

  const canManage = hasAnyRole(roles, MANAGEMENT_ROLES);
  const canManageStudents = hasAnyRole(roles, STUDENT_ROLES);
  const canCollectPayments = hasAnyRole(roles, PAYMENT_ROLES);
  // AUT-12 : le périmètre strictement financier d'`ADMIN_FINANCIER` (Tarifs,
  // Dashboard financier/Factures impayées...) n'est plus décidé ici lien par
  // lien — UX-79 : `ROUTE_ACCESS` (lib/roles.ts) filtre chaque lien, voir
  // `visibleGroups` plus bas.
  // SCH-53 : `TEACHER_ROLES` — groupe INDÉPENDANT des autres (jamais
  // combiné à `canManage`/`canCollectPayments`/`canManageFinances`), gate
  // uniquement la nouvelle entrée "Enseignant" ci-dessous.
  const isTeacher = hasAnyRole(roles, TEACHER_ROLES);
  // SCH-65 : compte TEACHER PUR — porte CE SEUL rôle, aucun autre cumulé
  // (AUT-26). DISTINCT de `isTeacher` ci-dessus : un compte qui cumule
  // TEACHER + un autre rôle (CASHIER, SCHOOL_ADMIN...) garde le menu complet
  // de cet autre rôle INCHANGÉ (test anti-régression AUT-26) — seul un
  // TEACHER pur bascule vers le menu Enseignant restructuré ci-dessous.
  const isTeacherOnly = roles.length === 1 && roles[0] === 'TEACHER';

  useEffect(() => {
    // AUT-09 : GET /enrollments/review (ENR-02) est désormais accessible à STAFF
    // aussi — le badge doit donc se peupler pour `canManageStudents`, pas
    // seulement `canManage` (sinon STAFF verrait le lien "Inscriptions" sans
    // jamais son compteur, malgré un accès backend réel).
    if (!canManageStudents || !accessToken) {
      setPendingEnrollments(null);
      return;
    }

    let cancelled = false;
    listEnrollmentsForReview(apiClient, { status: 'PENDING', pageSize: 1 }, accessToken)
      .then((result) => {
        if (!cancelled) setPendingEnrollments(result.total);
      })
      .catch(() => {
        if (!cancelled) setPendingEnrollments(null);
      });

    return () => {
      cancelled = true;
    };
  }, [canManageStudents, accessToken, apiClient, location.pathname]);

  // Rangement CHANGÉ par rapport à UX-04 (NavBar.tsx poussait dans l'ordre
  // Paramètres/Élèves/Comptabilité) — "Élèves & Inscriptions" passé EN
  // PREMIER intentionnellement (UX-09) : ne pas réordonner/re-répartir sans
  // mise à jour explicite de cette décision.
  // UX-21 : icônes emoji DÉCORATIVES (aria-hidden, voir Sidebar.tsx,
  // packages/ui) — mapping complet documenté dans docs/architecture.md,
  // section "UX-21". Choisies parmi des emoji Unicode ANCIENS (6.0/2010 ou
  // antérieur, sauf mention contraire) pour la compatibilité Android
  // ancienne demandée par le ticket — jamais de séquence ZWJ (🧑‍🎓, 👨‍👩‍👧...),
  // toujours un point de code unique.
  const groups: SidebarGroup[] = [];
  if (canManageStudents) {
    groups.push({
      key: 'students',
      label: NAV_GROUP_LABELS.students,
      badge: pendingEnrollments,
      badgeTestId: 'enrollments-pending-badge-menu',
      items: [
        // AUT-09 : "Vue d'ensemble" reste réservée à SCHOOL_ADMIN (dépend de
        // GET /classes et GET /schools/:id/school-years, tous deux
        // SCHOOL_ADMIN_ONLY, hors périmètre de ce ticket — voir le commentaire
        // au-dessus de ce composant).
        NAV.studentsOverview,
        NAV.students,
        // UX-47 : UN SEUL item — l'ancien lien "Réinscriptions" (UX-18,
        // `/reenrollments`, gardé par `canManage`) est FUSIONNÉ dans l'onglet
        // du même nom de cette page. Cet item garde la garde LA PLUS LARGE
        // (`canManageStudents`, celle de l'onglet par défaut "Nouvelles
        // demandes", AUT-09) ; c'est l'ONGLET "Réinscriptions" lui-même qui
        // reste réservé à SCHOOL_ADMIN (GET /enrollments/reenrollment-status,
        // ENR-10, toujours SCHOOL_ADMIN_ONLY) — voir Enrollments.tsx. Le badge
        // de compteur PENDING est CONSERVÉ tel quel.
        {
          ...NAV.enrollments,
          badge: pendingEnrollments,
          badgeTestId: 'enrollments-pending-badge',
        },
      ],
    });
  }
  // SCH-53 — nouvelle entrée "Enseignant", visible UNIQUEMENT pour un compte
  // portant `TEACHER` (cumulable avec n'importe quel autre rôle, AUT-26) —
  // mène vers `TeacherHome.tsx`, page d'échafaudage minimale (aucun contenu
  // fonctionnel réel pour ce ticket). Groupe à une seule entrée, même
  // précédent que la page d'accueil elle-même : pas de sous-menu à inventer
  // tant qu'aucun contenu fonctionnel (devoirs/notes/emploi du temps/
  // messagerie) n'existe.
  // SCH-65 — restructuration COMPLÈTE du groupe (5 entrées), à l'origine
  // réservée au compte TEACHER PUR (`isTeacherOnly`) via ce même ternaire.
  // SCH-69 (décision PO du 22/09) : le groupe complet s'affiche désormais
  // pour TOUT compte portant `TEACHER`, cumulé ou non — trouvé sur un compte
  // réel (STAFF+TEACHER+GESTION_ELEVES) qui ne voyait qu'un seul lien
  // "Accueil enseignant" au lieu du menu complet. `isTeacherOnly` n'intervient
  // PLUS dans le contenu de CE groupe ; il continue de gater le MASQUAGE du
  // `homeLink` générique et des autres groupes ci-dessous pour un compte
  // TEACHER pur, INCHANGÉ — un compte cumulé garde tout son menu habituel
  // intact, en plus de ce groupe.
  if (isTeacher) {
    groups.push({
      key: 'teacher',
      label: NAV_GROUP_LABELS.teacher,
      items: [
        NAV.teacherHome,
        NAV.teacherClasses,
        NAV.teacherSchedule,
        NAV.teacherAttendance,
        NAV.teacherHomework,
        NAV.teacherGrades,
        NAV.teacherMessaging,
      ],
    });
  }
  // UX-46 — restructuration complète du groupe "Comptabilité", 3 changements
  // structurels décidés par le PO le 25/08 (comparaison avec la référence
  // Claude Design) :
  // 1. L'en-tête du groupe devient lui-même un lien vers "Aperçu"
  //    (`headerTo`, nouveau mécanisme additif posé par ce ticket sur
  //    `NavigationSidebar`/`packages/ui`, MÊME soin d'additivité que
  //    `extraLinks`/`footerLinks`, UX-28/UX-43) — l'item "Aperçu" séparé,
  //    lui, est RETIRÉ (redondant avec l'en-tête désormais cliquable).
  // 2. "Paiements" (UX-16, page unique) est SCINDÉE en "Caisse"
  //    (`/payments`, route INCHANGÉE — reste LE flux d'encaissement guidé,
  //    tous les liens "aller payer" existants (`MissingInvoicesBanner`,
  //    `Home.tsx`, 2 des 3 cartes `AccountingOverview.tsx`) continuent de
  //    fonctionner SANS modification) et "Historique des encaissements"
  //    (`/payment-history`, NOUVELLE route — liste passive, voir
  //    `PaymentHistory.tsx`).
  // 3. "Tarifs" DÉMÉNAGE depuis le groupe "Paramètres" vers la FIN de ce
  //    groupe — contenu de `Fees.tsx` INCHANGÉ, seul son emplacement dans le
  //    menu change. Conséquence directe : la ligne `extraLinks` qui gérait
  //    "Tarifs" pour `ADMIN_FINANCIER` SANS `canManage` (UX-29, groupe
  //    "Paramètres" à une seule entrée) devient OBSOLÈTE et RETIRÉE — ce
  //    rôle a DÉJÀ `canCollectPayments` (`PAYMENT_ROLES`, AUT-12), donc
  //    DÉJÀ accès au groupe "Comptabilité" complet ; "Tarifs" y apparaît
  //    désormais NATURELLEMENT via la MÊME garde que les 3 autres items
  //    financiers (`canManage || canManageFinances`, `FINANCIAL_MANAGERS`
  //    côté backend — vérifié dans `routes/feeConfigurations.ts` AVANT ce
  //    changement, MÊME gate déjà utilisée pour Dashboard financier/Par
  //    classe/Factures impayées) — plus aucun mécanisme `extraLinks` dédié
  //    nécessaire pour ce rôle.
  if (canCollectPayments) {
    groups.push({
      key: 'accounting',
      label: NAV_GROUP_LABELS.accounting,
      headerTo: NAV.accountingOverview.to,
      items: [
        // AUT-12 : ces items correspondent à FINANCIAL_MANAGERS côté backend
        // (DSH-01/03/PAY-10) — filtrés via `ROUTE_ACCESS` (UX-79).
        NAV.financialDashboard,
        // DSH-04 : drill-down dashboard → classe → élève, même garde EXACTE
        // que "Dashboard financier" ci-dessus (FINANCIAL_MANAGERS côté
        // backend) — juste après lui, avant "Factures impayées".
        NAV.financialByClass,
        NAV.overdueInvoices,
        NAV.payments,
        // UX-46 — nouvelle page, EXTRAITE de l'ancienne `Payments.tsx`
        // (flux d'encaissement/historique combinés, UX-16) : "liste passive
        // des paiements déjà encaissés", même auto-garde `PAYMENT_COLLECTORS`
        // que "Caisse" — accessible aux MÊMES rôles, jamais un accès plus
        // restreint pour consulter un historique que pour encaisser.
        NAV.paymentHistory,
        // PAY-16 : dépôt/validation/rejet de preuves de paiement — MÊME garde
        // `PAYMENT_COLLECTORS` que "Caisse"/"Historique" (décision FINALE
        // actée avec le PO, ne pas revenir dessus), jamais `FINANCIAL_
        // MANAGERS`. Pure traçabilité, aucun impact Invoice/Payment.
        NAV.paymentProofs,
        // PAY-22 : traçabilité comptable du solde créditeur familial (point 3
        // du ticket) — MÊME garde FINANCIAL_MANAGERS que "Dashboard
        // financier"/"Par classe"/"Factures impayées" ci-dessus (jamais
        // `PAYMENT_COLLECTORS`, `CASHIER` exclu — cohérent avec le backend,
        // qui gère lui aussi la SEULE route qui GÈNÈRE une ligne CREDIT).
        NAV.creditLedger,
        NAV.fees,
      ],
    });
  }
  // UX-29 — Étape 0 confirme (compté réellement pour chaque rôle, pas supposé) :
  // SCHOOL_ADMIN (canManage) garde les 11 items de ce groupe, INCHANGÉ ; STAFF/
  // CASHIER ne le voient JAMAIS (ni canManage ni canManageFinances, groupe absent
  // depuis toujours) ; SEUL `ADMIN_FINANCIER` (canManageFinances SANS canManage,
  // AUT-12) ne voyait qu'UN SEUL item ("Tarifs") — en-tête de groupe + sous-lien
  // redondants, même classe de trou que "Documents"/web-parent (UX-28). Option A
  // retenue : un lien de premier niveau (`extraLinks`, prop déjà posée par UX-28
  // sur `NavigationSidebar`, packages/ui — PAS étendue ici) REMPLACE le groupe
  // UNIQUEMENT dans cette branche précise ; SCHOOL_ADMIN garde le `SidebarGroup`
  // complet ci-dessous, strictement inchangé (Tarifs y reste inclus, jamais
  // dupliqué en `extraLinks`).
  // UX-42 — "Mes informations personnelles" (PAR-14) et "Changer mon mot de
  // passe" (AUT-16) apparaissaient comme DEUX liens plats de premier niveau
  // (même classe de trou que "Documents"/"Réglages"/"Paiements" côté
  // web-parent, UX-28/UX-30/UX-31) — FUSIONNÉS en UN SEUL lien "Compte" vers
  // la page hub `Account.tsx` (2 tuiles, MÊME patron EXACT que `Settings.tsx`/
  // web-parent, UX-27). Les DEUX routes de détail (`/profile`/
  // `/change-password`) restent INCHANGÉES — seul leur point d'entrée dans la
  // nav change. Gardé UNIQUEMENT par `isAuthenticated` (comme `homeLink`),
  // jamais restreint à un rôle — TOUT compte authentifié (SCHOOL_ADMIN/STAFF/
  // CASHIER/ADMIN_FINANCIER) a un profil et un mot de passe à gérer.
  //
  // UX-43 — CORRECTIF DE POSITIONNEMENT : "Compte" avait été placé en
  // `extraLinks` (lien de PREMIER NIVEAU, sous "Accueil") par erreur — la
  // référence Claude Design le place en PIED de nav, juste au-dessus de
  // "Se déconnecter". Déplacé vers `footerLinks` (nouvelle prop additive,
  // `packages/ui`, voir son doc-comment — AUCUN mécanisme de premier niveau
  // ne peut se positionner en pied de nav). Comportement INCHANGÉ (même
  // route/icône/garde `isAuthenticated`), seul l'EMPLACEMENT change.
  //
  // UX-46 — `extraLinks` ("Tarifs" pour `ADMIN_FINANCIER` SANS `canManage`,
  // UX-29) RETIRÉ : "Tarifs" vit désormais DANS le groupe "Comptabilité"
  // (voir plus haut), auquel ce rôle a déjà accès via `canCollectPayments` —
  // plus aucun lien de premier niveau nécessaire pour ce cas, l'anti-pattern
  // "groupe Paramètres à une seule entrée" qu'UX-29 corrigeait n'a plus lieu
  // d'être (Paramètres ne contient plus "Tarifs" du tout, pour aucun rôle).
  const footerLinks: SidebarLink[] = [
    ...(isAuthenticated ? [NAV.account] : []),
  ];

  // AUT-12 : Classes/Années scolaires/Documents requis/Équipe/MFA/Journal
  // d'audit (élève/inscriptions/équipe, hors périmètre strict de
  // `ADMIN_FINANCIER`) restent conditionnés à `canManage` seul,
  // individuellement. UX-29 : ce groupe complet n'est désormais poussé QUE
  // pour `canManage` (SCHOOL_ADMIN) — `ADMIN_FINANCIER` n'a plus AUCUN accès
  // à ce groupe (UX-46 : "Tarifs", le seul item qui lui était jusqu'ici
  // accessible ici, en a été retiré — voir groupe "Comptabilité" plus haut).
  if (canManage) {
    groups.push({
      key: 'settings',
      label: NAV_GROUP_LABELS.settings,
      items: [
        NAV.settings,
        // SCH-05 — assistant d'onboarding guidé (4 étapes : Logo/nom, Classes,
        // Tarifs, Invitation staff optionnelle), SCHOOL_ADMIN_ONLY côté backend
        // (GET /schools/:id/onboarding-status) — placé juste après "Aperçu",
        // même garde `canManage` (jamais `canManageFinances` seul, ce rôle n'a
        // aucun accès Classes/Équipe, deux des 4 étapes du parcours).
        NAV.onboarding,
        // SCH-02 — personnalisation (nom/devise/logo), SCHOOL_ADMIN_ONLY côté
        // backend (PATCH /schools/:id, POST /schools/:id/logo) — même garde que
        // Classes/Années scolaires ci-dessous, pas FINANCIAL_MANAGERS.
        NAV.branding,
        // SCH-66 — renommé "Classes" -> "Gestion pédagogique" (retour PO :
        // l'écran réunit désormais classes/matières/prof principal/emploi du
        // temps sur un seul point d'entrée cohérent) — MÊME route
        // (`/classes`, `ManageClasses.tsx`), simple changement de libellé.
        NAV.classes,
        NAV.schoolYears,
        // UX-46 : "Tarifs" DÉMÉNAGÉ vers la FIN du groupe "Comptabilité" (voir
        // plus haut) — retiré d'ici, jamais dupliqué entre les deux groupes.
        // PAY-18 — configuration des moyens de paiement en ligne, SCHOOL_ADMIN_ONLY
        // côté backend (comme demandé explicitement par le ticket, contrairement à
        // Tarifs/Dashboard financier — pas FINANCIAL_MANAGERS) : gardé par
        // `canManage` seul, jamais `canManageFinances`.
        NAV.paymentMethods,
        NAV.documentRequirements,
        NAV.invitations,
        // WSP-03 — diffusion WhatsApp générale à l'échelle de l'école,
        // SCHOOL_ADMIN_ONLY côté backend (POST/GET /schools/:id/
        // whatsapp-broadcasts) — même garde que Classes/Équipe ci-dessus, pas
        // FINANCIAL_MANAGERS (canal de communication générale, pas financier).
        NAV.whatsappBroadcasts,
        // AUT-05 — activation MFA, gardée ici (canManage) même si la route
        // elle-même reste accessible à tout compte authentifié côté backend
        // (voir routes/mfa.ts).
        NAV.mfaSetup,
        // AUT-07 — journal d'audit, "fonctionnalité secondaire, pas
        // prioritaire en UX" (règle explicite du ticket) : placé en DERNIER
        // dans ce groupe, SCHOOL_ADMIN_ONLY côté backend (GET /audit-logs),
        // voir AuditLogs.tsx.
        NAV.auditLogs,
      ],
    });
  }

  // UX-79 — chaque lien est filtré par `ROUTE_ACCESS` (lib/roles.ts), la MÊME
  // table que la garde de route `RequireRouteAccess` : la Sidebar ne peut plus
  // proposer un lien qui redirigerait vers l'accueil. Les conditions de GROUPE
  // ci-dessus restent (elles décident quels menus existent pour un rôle).
  const visibleGroups = groups
    .map((group) => ({ ...group, items: group.items.filter((item) => canAccessRoute(roles, item.to)) }))
    .filter((group) => group.items.length > 0);

  return (
    <NavigationSidebar
      brandLabel="Mon Ecolier"
      // SCH-65 : masqué pour un compte TEACHER pur — "Accueil enseignant"
      // (groupe "Enseignant" ci-dessus) est déjà SON point d'entrée dédié, le
      // lien générique "Accueil" (dashboard SCHOOL_ADMIN/STAFF/CASHIER) n'a
      // aucun sens pour ce profil.
      homeLink={isTeacherOnly ? undefined : NAV.home}
      groups={visibleGroups}
      footerLinks={footerLinks}
    />
  );
}
